import { Component, OnInit } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { AuthenticationService } from "~/core/services/authentication.service";
import { StorageService } from "~/core/services/storage.service";
import { ThemeService } from "~/core/services/theme.service";
import { GlobalLoginComponent } from "~/containers/global-login/global-login.component";
import { UserProfile } from "~/core/constants/user-profile";
import { FlexModule } from "@ngbracket/ngx-layout";
import { CompanyService } from "~/core/services/company.service";
import { UtilsService } from "~/core/helpers/utils";
import packageJson from "../../../../package.json";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  imports: [
    ReactiveFormsModule,
    MatIcon,
    MatButtonModule,
    MatMenuModule,
    GlobalLoginComponent,
    FlexModule,
    TranslateModule,
  ],
})
export class LoginComponent implements OnInit {
  loading = false;
  logo: any;

  /** Versão do `package.json` (ex.: v4.0.7-17), fallback v0.0.0 */
  readonly appVersionLabel = `v${packageJson.version ?? "0.0.0"}`;

  languageOptions = [
    { id: "pt-br", name: "GENERAL.PORTUGUESE", image: "pt-br", active: false },
    { id: "en", name: "GENERAL.ENGLISH", image: "en", active: false },
    { id: "es", name: "GENERAL.SPANISH", image: "es", active: false },
    { id: "fr", name: "GENERAL.FRENCH", image: "fr", active: false },
    { id: "es-mx", name: "GENERAL.SPANISH_MEX", image: "es-mx", active: false },
  ];

  constructor(
    private auth: AuthenticationService,
    private router: Router,
    private translate: TranslateService,
    public themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    ThemeService.toggleDark();
    const theme = ThemeService.isLightTheme ? "light" : "dark";
    this.logo = CompanyService.getLogo(theme);
    this.loadLanguageOptions();
    if (this.auth.isLoggedIn()) {
      this.success();
      return;
    }
  }

  loadLanguageOptions(): void {
    if (UtilsService.isLicenseMexican()) {
      this.languageOptions = UtilsService.languageOptionsMexican;
    }
    this.updateSelectedLanguage();
  }

  getSelectedLanguage(): {
    id: string;
    name: string;
    image: string;
    active: boolean;
  } {
    return (
      this.languageOptions.find((lang) => lang.active) ||
      this.languageOptions[0]
    );
  }

  getSelectedLanguageIcon(): string {
    const selected = this.getSelectedLanguage();
    return selected
      ? `assets/plus2/ic-id-${selected.image.toLowerCase()}.avif`
      : "";
  }

  languageFlagSrc(image: string): string {
    return `assets/plus2/ic-id-${image.toLowerCase()}.avif`;
  }

  setLanguage(languageId: string): void {
    StorageService.currentLanguage = languageId;
    this.translate.use(languageId);
    this.updateSelectedLanguage(languageId);
  }

  updateSelectedLanguage(languageId?: string): void {
    const currentLang = languageId || StorageService.currentLanguage;
    for (const language of this.languageOptions) {
      language.active = language.id === currentLang;
    }
  }

  success(): void {
    if (this.auth.getCurrentUser()?.type === UserProfile.CLIENT) {
      this.auth.logout();
      this.router.navigate(["/"]).then(() => {
        ThemeService.toggleLight();
      });
    } else {
      this.router.navigate(["back-office", "home"]).then(() => {
        ThemeService.toggleLight();
      });
    }
  }

  protected readonly ThemeService = ThemeService;
}
